import axios from 'axios';
import { supabase } from '../lib/supabase';

const OPENAI_API_KEY = import.meta.env?.VITE_OPENAI_API_KEY;
const TTS_API_URL = 'https://api.openai.com/v1/audio/speech';
const STORAGE_BUCKET = 'glossary-pronunciations';

/**
 * Generate pronunciation audio for a single glossary term using OpenAI TTS.
 * Uploads the audio to Supabase storage and updates the glossary_terms row.
 *
 * @param {string} termId - UUID of the glossary_terms row
 * @param {string} term - The word to pronounce
 * @returns {Promise<{ audioUrl: string }>}
 */
export const generateAudioForTerm = async (termId, term) => {
  // Mark as generating
  await supabase
    ?.from('glossary_terms')
    ?.update({
      audio_generation_status: 'generating',
      updated_at: new Date()?.toISOString()
    })
    ?.eq('id', termId);

  try {
    // Call OpenAI TTS API directly (not via Lambda — TTS is not a supported Lambda feature)
    const response = await axios?.post(
      TTS_API_URL,
      {
        model: 'tts-1',
        input: term,
        voice: 'alloy',
        response_format: 'mp3'
      },
      {
        headers: {
          Authorization: `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json'
        },
        responseType: 'arraybuffer'
      }
    );

    // Convert arraybuffer to Blob
    const audioBlob = new Blob([response?.data], { type: 'audio/mpeg' });

    // Build a unique storage path: glossary-pronunciations/{termId}.mp3
    const storagePath = `${termId}.mp3`;

    // Upload to Supabase storage
    const { error: uploadError } = await supabase?.storage
      ?.from(STORAGE_BUCKET)
      ?.upload(storagePath, audioBlob, {
        contentType: 'audio/mpeg',
        upsert: true
      });

    if (uploadError) {
      throw uploadError;
    }

    // Get the public URL
    const { data: urlData } = supabase?.storage
      ?.from(STORAGE_BUCKET)
      ?.getPublicUrl(storagePath);

    const audioUrl = urlData?.publicUrl;

    // Update glossary_terms with audio_url, completed status, and quality flag
    const { error: updateError } = await supabase
      ?.from('glossary_terms')
      ?.update({
        audio_url: audioUrl,
        audio_generation_status: 'completed',
        audio_quality_flag: 'ai_generated',
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', termId);

    if (updateError) {
      throw updateError;
    }

    return { audioUrl };
  } catch (error) {
    // Mark as failed so admins can retry
    await supabase
      ?.from('glossary_terms')
      ?.update({
        audio_generation_status: 'failed',
        updated_at: new Date()?.toISOString()
      })
      ?.eq('id', termId);

    console.error(`Audio generation failed for term "${term}":`, error);
    throw error;
  }
};

/**
 * Generate pronunciation audio for a batch of glossary terms.
 * Processes terms sequentially to avoid rate limits.
 * Non-blocking: errors on individual terms are caught and logged.
 *
 * @param {Array<{ id: string, term: string }>} terms - Array of term objects
 * @param {Function} [onProgress] - Optional callback(completed, total, currentTerm)
 * @returns {Promise<{ succeeded: number, failed: number }>}
 */
export const generateAudioForTerms = async (terms, onProgress) => {
  if (!terms?.length) return { succeeded: 0, failed: 0 };

  let succeeded = 0;
  let failed = 0;

  for (let i = 0; i < terms?.length; i++) {
    const { id, term } = terms?.[i];
    onProgress?.(i, terms?.length, term);

    try {
      await generateAudioForTerm(id, term);
      succeeded++;
    } catch {
      failed++;
      // Continue with remaining terms
    }

    // Small delay between requests to respect rate limits
    if (i < terms?.length - 1) {
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  }

  onProgress?.(terms?.length, terms?.length, null);
  return { succeeded, failed };
};

/**
 * Retry audio generation for all terms with status 'failed' or 'pending'.
 * Useful for admin-triggered regeneration from Glossary Management.
 *
 * @param {string|null} playId - Optional play UUID to scope the retry
 * @param {Function} [onProgress] - Optional progress callback
 * @returns {Promise<{ succeeded: number, failed: number }>}
 */
export const retryFailedAudioGeneration = async (playId, onProgress) => {
  try {
    let query = supabase
      ?.from('glossary_terms')
      ?.select('id, term')
      ?.in('audio_generation_status', ['failed', 'pending']);

    if (playId) {
      query = query?.eq('play_id', playId);
    }

    const { data: terms, error } = await query;

    if (error) throw error;
    if (!terms?.length) return { succeeded: 0, failed: 0 };

    return await generateAudioForTerms(terms, onProgress);
  } catch (error) {
    console.error('Error retrying audio generation:', error);
    throw error;
  }
};
